/**
 * D-EZ-95 — THE RECORD BOOK'S RECORDS.
 *
 * Single-season records (most wins, points, poles, top fives, top tens, miles
 * led and money in one season) and career records (the same, summed, plus
 * starts and titles). Each record lists the holder AND the runners-up, so a
 * screen can say "two short of the record" as well as "the record".
 */

import type { DriverRegistry, SeasonLine } from './registry.ts';
import type { HistoryRace } from './source.ts';

export type RecordStat =
  | 'wins'
  | 'points'
  | 'poles'
  | 'top5'
  | 'top10'
  | 'turnsLed'
  | 'winningsMb'
  | 'starts'
  | 'championships';

export interface RecordHolder {
  driverId: string;
  name: string;
  value: number;
  /** For a single-season record, the season it was set in. */
  seasonNumber?: number;
}

export interface LeagueRecord {
  stat: RecordStat;
  title: string;
  scope: 'season' | 'career';
  /** Ties included: everyone level at the top. */
  holders: RecordHolder[];
  /** The next best, ranked, up to five. */
  chasers: RecordHolder[];
}

const SEASON_TITLES: Partial<Record<RecordStat, string>> = {
  wins: 'Most wins in a season',
  points: 'Most points in a season',
  poles: 'Most poles in a season',
  top5: 'Most top fives in a season',
  top10: 'Most top tens in a season',
  turnsLed: 'Most miles led in a season',
  winningsMb: 'Most won in a season',
};

const CAREER_TITLES: Record<RecordStat, string> = {
  wins: 'Most career wins',
  points: 'Most career points',
  poles: 'Most career poles',
  top5: 'Most career top fives',
  top10: 'Most career top tens',
  turnsLed: 'Most career miles led',
  winningsMb: 'Most career winnings',
  starts: 'Most career starts',
  championships: 'Most championships',
};

function rank(list: RecordHolder[], scope: 'season' | 'career', stat: RecordStat, title: string): LeagueRecord {
  const sorted = list
    .filter((h) => h.value > 0)
    .sort((a, b) => b.value - a.value || (a.seasonNumber ?? 0) - (b.seasonNumber ?? 0) || a.name.localeCompare(b.name));
  const top = sorted[0]?.value;
  const holders = top === undefined ? [] : sorted.filter((h) => h.value === top);
  return { stat, title, scope, holders, chasers: sorted.slice(holders.length, holders.length + 5) };
}

/** `turnsLed` is shown in miles (a turn is ten miles, RB/ii). */
function lineValue(l: SeasonLine, stat: RecordStat): number {
  if (stat === 'turnsLed') return l.turnsLed * 10;
  return (l as unknown as Record<string, number>)[stat] ?? 0;
}

/**
 * Single-season records. Only COMPLETE seasons can hold one, but pass
 * `includeLive` to see a season in progress chasing them.
 */
export function seasonRecords(registry: DriverRegistry, opts: { includeLive?: boolean } = {}): LeagueRecord[] {
  const stats = Object.keys(SEASON_TITLES) as RecordStat[];
  return stats.map((stat) => {
    const list: RecordHolder[] = [];
    for (const e of registry.all) {
      for (const l of e.bySeason) {
        if (!l.complete && !opts.includeLive) continue;
        list.push({ driverId: e.driverId, name: e.name, value: lineValue(l, stat), seasonNumber: l.seasonNumber });
      }
    }
    return rank(list, 'season', stat, SEASON_TITLES[stat] as string);
  });
}

/** Career records, counting pre-league priors where a scenario supplied them. */
export function careerRecords(registry: DriverRegistry): LeagueRecord[] {
  const stats = Object.keys(CAREER_TITLES) as RecordStat[];
  return stats.map((stat) =>
    rank(
      registry.all.map((e) => ({
        driverId: e.driverId,
        name: e.name,
        value: stat === 'turnsLed' ? e.career.turnsLed * 10 : (e.career as unknown as Record<string, number>)[stat] ?? 0,
      })),
      'career',
      stat,
      CAREER_TITLES[stat],
    ),
  );
}

/** A driver's career-best season (best championship rank; ties on points). */
export function careerBestSeason(registry: DriverRegistry, driverId: string): SeasonLine | null {
  return registry.byId[driverId]?.bestSeason ?? null;
}

/** Single-race records worth a line in the book. */
export interface RaceRecords {
  /** The deepest starting spot anyone has won from. */
  winFromFurthestBack: { driverId: string; name: string; start: number; seasonNumber: number; raceNumber: number; raceName: string } | null;
  /** The most cars collected in one turn, anywhere. */
  biggestWreck: { cars: number; seasonNumber: number; raceNumber: number; raceName: string; trackName: string } | null;
  /** Most turns led in a single race, shown in miles. */
  mostMilesLedInARace: { driverId: string; name: string; miles: number; seasonNumber: number; raceNumber: number; raceName: string } | null;
}

export function raceRecords(races: readonly HistoryRace[]): RaceRecords {
  let back: RaceRecords['winFromFurthestBack'] = null;
  let wreck: RaceRecords['biggestWreck'] = null;
  let led: RaceRecords['mostMilesLedInARace'] = null;
  for (const r of races) {
    const winner = r.lines.find((l) => l.finish === 1);
    if (winner?.start && (!back || winner.start > back.start)) {
      back = { driverId: winner.driverId, name: winner.name, start: winner.start, seasonNumber: r.seasonNumber, raceNumber: r.raceNumber, raceName: r.raceName };
    }
    if (r.biggestWreck && (!wreck || r.biggestWreck.cars > wreck.cars)) {
      wreck = { cars: r.biggestWreck.cars, seasonNumber: r.seasonNumber, raceNumber: r.raceNumber, raceName: r.raceName, trackName: r.trackName };
    }
    for (const l of r.lines) {
      if (!led || l.turnsLed * 10 > led.miles) {
        led = { driverId: l.driverId, name: l.name, miles: l.turnsLed * 10, seasonNumber: r.seasonNumber, raceNumber: r.raceNumber, raceName: r.raceName };
      }
    }
  }
  return { winFromFurthestBack: back, biggestWreck: wreck, mostMilesLedInARace: led };
}
